import { api } from "./base-config";
import { Address } from "common/models/Address";
import { AddressDTO } from "common/dto/Address";
import { mapStringToGender } from "common/enums/GenderEnum";
import { Patient } from "model/patient.model";
import { PersonDTO } from "api/dto/PersonDTO";
import { mapStringToLanguage } from "config/translations/I18n";

/** ---- API CALLS ---- */
export const quickSearch = async (searchText: string): Promise<Patient[]> => {
  return api
    .get("/resources/secured/rs/patients/quicksearchall", {
      params: { searchText }
    })
    .then(response =>
      response.data.patients.map((searchPatientDTO: SearchPatientDTO) =>
        mapFromSearchPatientDtoToPatientModel(searchPatientDTO)
      )
    );
};

export const findPatientById = (patientId: number): Promise<Patient> => {
  return api
    .get(`/resources/secured/rs/patients/${patientId}`)
    .then(response => mapFromPatientDtoToPatientModel(response.data));
};

/** ---- API MAPPING ---- */
const mapFromSearchPatientDtoToPatientModel = (
  searchPatientDTO: SearchPatientDTO
): Patient => {
  const patient = mapToBasicPatient(searchPatientDTO);
  patient.id = searchPatientDTO.id;

  const address = new Address();
  address.id = searchPatientDTO.id;
  address.street = searchPatientDTO.street;
  address.number = searchPatientDTO.number;
  address.zip = searchPatientDTO.zip;
  address.municipality = searchPatientDTO.municipality;
  patient.address = address;

  return patient;
};

const mapFromPatientDtoToPatientModel = (patientDTO: PatientDTO): Patient => {
  const { person } = patientDTO;
  const patient = mapToBasicPatient(person);
  patient.id = patientDTO.id;
  patient.inss = person.inss;
  patient.language = mapStringToLanguage(patientDTO.recordLanguage);

  return patient;
};

const mapToBasicPatient = (dto: SearchPatientDTO | PersonDTO): Patient => {
  const patient: Patient = new Patient();
  patient.firstName = dto.firstName;
  patient.lastName = dto.lastName;
  patient.gender = mapStringToGender(dto.gender);
  patient.birthDate = dto.birthDate;

  return patient;
};

/** ---- API DTO ---- */
interface SearchPatientDTO {
  readonly id: number;
  readonly firstName: string;
  readonly lastName: string;
  readonly birthDate?: Date;
  readonly birthDateMask?: string;
  readonly gender?: string;
  readonly inss?: string;
  readonly street?: string;
  readonly number?: string;
  readonly zip?: string;
  readonly municipality?: string;
  readonly archived?: boolean;
}

interface PatientDTO {
  readonly id: number;
  readonly person: PersonDTO;
  readonly address?: AddressDTO;
  readonly archived?: boolean;
  readonly recordLanguage?: string;
}
